import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import {
  CloudAlertIcon,
  ExternalLinkIcon,
  Link2Icon,
  TriangleAlertIcon,
  XIcon,
} from 'lucide-react';
import { Button } from '@monksflow/monks-ui';
import {
  levelLabel,
  NAME_ALREADY_ON_PLATFORM,
  type JobIssue,
  type JobIssueSeverity,
} from '@/entities/media-campaign';
import { countSeverity, type RowIssue } from '../lib/flatten';
import { SEVERITY_TONE } from './severity-tone';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';

/**
 * Every finding used to look the same: one card, one muted border, a coloured icon and
 * nothing else. A blocker and a note about what an id points at read identically, so a
 * list that said "4 to fix, 3 to check" gave no way to tell which four.
 *
 * The card carries the severity now — an edge and a tint for what blocks, plain for what
 * does not — and the list is grouped under the same two words the count uses.
 */
const SEVERITY_CARD: Record<JobIssueSeverity, string> = {
  error: 'border-l-2 border-l-destructive bg-destructive/5 hover:border-destructive',
  warning: 'border-l-2 border-l-warning hover:border-primary',
  advice: 'hover:border-primary',
};

const buildGroups = (
  t: TFunction<'projects'>,
): { severity: JobIssueSeverity; heading: string }[] => [
  { severity: 'error', heading: t('deliveryJobReview.jobIssuesPanel.group.error') },
  { severity: 'warning', heading: t('deliveryJobReview.jobIssuesPanel.group.warning') },
  { severity: 'advice', heading: t('deliveryJobReview.jobIssuesPanel.group.advice') },
];

/**
 * What a house standard adds over a platform rule: why it exists, how to satisfy it,
 * where it is written down and who owns it.
 *
 * Renders nothing for a platform finding, which has none of this — the platform states
 * a rule and offers no remedy. That contrast is the point of the block: a reviewer can
 * tell at a glance whether a failure is negotiable and who to ask.
 *
 * `div`s rather than `p`s: this sits inside the button that focuses the offending cell,
 * and a `p` there is invalid markup.
 */
function GuidelineRemediation({ issue }: { issue: JobIssue }) {
  const { t } = useTranslation('projects');
  if (issue.source !== 'guideline') return null;
  if (!issue.why && !issue.howToFix && !issue.referenceUrl && !issue.ownerName) return null;

  return (
    <span className="mt-1 flex flex-col gap-1 border-l-2 border-border pl-2">
      {issue.why && <span className="text-xs italic text-muted-foreground">{issue.why}</span>}
      {issue.howToFix && <span className="text-xs text-foreground">{issue.howToFix}</span>}
      <span className="flex flex-wrap items-center gap-x-2 text-xs text-muted-foreground">
        {issue.ownerName && <span>{issue.ownerName}</span>}
        {issue.referenceUrl && (
          // Stops the click reaching the button, which would scroll the sheet away
          // from the link the reviewer just opened.
          <a
            href={issue.referenceUrl}
            target="_blank"
            rel="noreferrer"
            onClick={(event) => event.stopPropagation()}
            className="inline-flex items-center gap-0.5 text-primary hover:underline"
          >
            {t('deliveryJobReview.jobIssuesPanel.reference')}
            <ExternalLinkIcon className="size-3" aria-hidden />
          </a>
        )}
      </span>
    </span>
  );
}

/**
 * The answer to a name that is already taken: bind this row to that object, so
 * publishing changes it rather than making a second.
 *
 * Deliberately a button rather than something that happens on its own. A name is a weak
 * identity, and adopting a live campaign because two strings matched is the one mistake
 * with no undo — so a person has to say so.
 */
function LinkToExisting({
  nodeId,
  onLink,
  busy,
}: {
  nodeId: string;
  onLink: (nodeId: string) => void;
  busy: string | null;
}) {
  const { t } = useTranslation('projects');
  return (
    <div className="mt-1 flex justify-end">
      <Button
        size="sm"
        variant="outline"
        disabled={busy !== null}
        onClick={() => onLink(nodeId)}
        data-testid={`job-issue-link-${nodeId}`}
      >
        <Link2Icon aria-hidden />
        {busy === nodeId
          ? t('deliveryJobReview.jobIssuesPanel.linking')
          : t('deliveryJobReview.jobIssuesPanel.linkToExisting')}
      </Button>
    </div>
  );
}

export interface JobIssuesPanelProps {
  open: boolean;
  platform: string;
  issues: RowIssue[];
  /** Issues on this node are shown first — the row whose badge was clicked. */
  focusNodeId: string | null;
  /** Switches to the issue's level, then scrolls to the cell and highlights it. */
  onFocusCell: (entry: RowIssue) => void;
  /** Absent where linking is not offered, e.g. a job nobody may still change. */
  onLinkNode?: (nodeId: string) => void;
  /** The row whose link is in flight, so its button alone says so. */
  linkingNodeId?: string | null;
  onClose: () => void;
}

/**
 * Every problem in the current platform's sheet, each one a link to the cell
 * that fixes it.
 *
 * A wide sheet can hide a blank required field a long way off-screen, so the
 * count in the row's badge is only useful if it leads somewhere. Clicking an
 * entry scrolls that cell into view and highlights it.
 */
export function JobIssuesPanel({
  open,
  platform,
  issues,
  focusNodeId,
  onFocusCell,
  onLinkNode,
  linkingNodeId = null,
  onClose,
}: JobIssuesPanelProps) {
  const { t } = useTranslation('projects');
  useEffect(() => {
    if (!open) return;
    const handler = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, onClose]);

  if (typeof document === 'undefined') return null;

  // The row that was clicked floats to the top; everything else keeps sheet
  // order so the list still reads as a walk down the plan.
  const ordered = [
    ...issues.filter((entry) => entry.nodeId === focusNodeId),
    ...issues.filter((entry) => entry.nodeId !== focusNodeId),
  ];

  const { errors, warnings: toCheck, advice } = countSeverity(ordered);
  // A clean plan can still carry advice, and the badge that opens this panel then counts
  // it, so a heading of "0 to fix" would contradict the thing that was clicked.
  const adviceOnly = errors === 0 && toCheck === 0 && advice > 0;

  // Grouped by what the reader has to do about it, keeping the clicked row first inside
  // each group so the badge still leads somewhere.
  const grouped = buildGroups(t)
    .map((group) => ({
      ...group,
      entries: ordered.filter((entry) => entry.issue.severity === group.severity),
    }))
    .filter((group) => group.entries.length > 0);

  return createPortal(
    // Portalled to `body` because the sheet sits inside a transformed motion
    // container, which would become the containing block for a fixed element
    // and mis-position the drawer.
    <aside
      className={`fixed bottom-0 right-0 top-0 z-50 flex w-96 flex-col border-l border-border bg-card shadow-xl transition-transform duration-slow ease-out ${
        open ? 'translate-x-0' : 'pointer-events-none translate-x-full'
      }`}
      data-testid="job-issues-panel"
      role="dialog"
      aria-label={t('deliveryJobReview.jobIssuesPanel.issuesInThisSheet')}
      aria-hidden={!open}
    >
      <header className="flex items-start justify-between gap-3 border-b border-border px-4 py-3">
        <div className="min-w-0">
          <div className="text-xs uppercase tracking-wide text-muted-foreground">
            {t('deliveryJobReview.jobIssuesPanel.issues')}
          </div>
          <div className="truncate text-sm font-medium text-foreground">
            {adviceOnly
              ? t('deliveryJobReview.jobIssuesPanel.forInformation', { count: advice })
              : t('deliveryJobReview.jobIssuesPanel.toFix', { count: errors })}
            {toCheck > 0
              ? t('deliveryJobReview.jobIssuesPanel.andToCheck', { count: toCheck })
              : ''}
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label={t('deliveryJobReview.jobIssuesPanel.closeIssues')}
          className="flex size-7 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <XIcon className="size-4" aria-hidden />
        </button>
      </header>

      <div className="flex-1 overflow-y-auto p-3">
        {ordered.length === 0 ? (
          <p className="px-1 py-6 text-center text-sm text-muted-foreground">
            {t('deliveryJobReview.jobIssuesPanel.nothingToFixOnThisPlatform')}
          </p>
        ) : (
          <ul className="flex flex-col gap-1.5">
            {grouped.flatMap((group) => [
              <li
                key={`heading-${group.severity}`}
                className="px-1 pb-0.5 pt-2 text-xs font-medium uppercase tracking-wide text-muted-foreground first:pt-0"
              >
                {t('deliveryJobReview.jobIssuesPanel.groupHeading', {
                  heading: group.heading,
                  count: group.entries.length,
                })}
              </li>,
              ...group.entries.map((entry) => (
                <li key={`${entry.nodeId}-${entry.issue.field ?? 'node'}-${entry.issue.message}`}>
                  <button
                    type="button"
                    onClick={() => onFocusCell(entry)}
                    data-testid={`job-issue-${entry.nodeId}-${entry.issue.field ?? 'node'}`}
                    className={`flex w-full flex-col gap-1 rounded-lg border border-border-light px-3 py-2 text-left transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${SEVERITY_CARD[entry.issue.severity]}`}
                  >
                    <span className="flex items-center gap-1.5">
                      {/* The platform's own objection gets its own mark: it is a
                        different kind of fact from one of our rules, and it is the
                        one the reviewer can neither argue with nor look up. */}
                      {entry.issue.source === 'platform' ? (
                        <CloudAlertIcon
                          className={`size-3.5 shrink-0 ${SEVERITY_TONE[entry.issue.severity]}`}
                          aria-label={t('deliveryJobReview.jobIssuesPanel.reportedByThePlatform')}
                        />
                      ) : (
                        <TriangleAlertIcon
                          className={`size-3.5 shrink-0 ${SEVERITY_TONE[entry.issue.severity]}`}
                          aria-hidden
                        />
                      )}
                      <span className="truncate text-xs text-muted-foreground">
                        {levelLabel(platform, entry.level)} · {entry.nodeName}
                        {entry.issue.source === 'platform' &&
                          t('deliveryJobReview.jobIssuesPanel.platformSuffix')}
                      </span>
                    </span>
                    {entry.fieldLabel && (
                      <span className="truncate text-sm font-medium">{entry.fieldLabel}</span>
                    )}
                    <span className="text-xs text-muted-foreground">{entry.issue.message}</span>
                    <GuidelineRemediation issue={entry.issue} />
                  </button>
                  {entry.issue.ruleId === NAME_ALREADY_ON_PLATFORM && onLinkNode && (
                    <LinkToExisting
                      nodeId={entry.nodeId}
                      onLink={onLinkNode}
                      busy={linkingNodeId}
                    />
                  )}
                </li>
              )),
            ])}
          </ul>
        )}
      </div>
    </aside>,
    document.body,
  );
}
